import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { TESTIMONIALS_FALLBACK, type ApiTestimonial } from "@/lib/testimonials-fallback";
import SmartImage from "@/components/ui/smart-image";

function StarRating({ rating }: { rating: number }) {
  return (
    <div
      className="flex gap-0.5 mb-2"
      role="img"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < rating ? "fill-warning text-warning" : "fill-muted text-muted-foreground/40"}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}

function TestimonialCard({ item }: { item: ApiTestimonial }) {
  return (
    <figure
      className="bg-muted/20 rounded-2xl p-6 sm:p-8 apple-shadow flex flex-col"
      data-testid={`testimonial-${item.id}`}
      itemScope
      itemType="https://schema.org/Review"
    >
      <div className="flex items-center mb-6">
        {item.photoUrl ? (
          <SmartImage
            src={item.photoUrl}
            alt={`${item.name} - happy RealtyBlocks customer from ${item.city}`}
            width={96}
            height={96}
            sizes="48px"
            widths={[48, 96, 144]}
            className="w-12 h-12 rounded-full object-cover mr-4"
            style={{ objectPosition: "center 25%" }}
          />
        ) : (
          <div
            aria-hidden="true"
            className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4 text-primary font-semibold"
          >
            {initials(item.name) || "RB"}
          </div>
        )}
        <figcaption>
          <div className="font-semibold text-foreground" itemProp="author" itemScope itemType="https://schema.org/Person">
            <span itemProp="name">{item.name}</span>
          </div>
          <p className="text-sm text-muted-foreground">{item.city}</p>
        </figcaption>
      </div>
      <div className="flex-1">
        <StarRating rating={item.rating} />
        <meta itemProp="reviewRating" content={String(item.rating)} />
        <blockquote className="text-muted-foreground" itemProp="reviewBody">
          {item.quote}
        </blockquote>
      </div>
    </figure>
  );
}

export default function Testimonials() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<ApiTestimonial[]>({
    queryKey: ["/api/testimonials", { featured: 1 }],
    queryFn: async () => {
      const res = await fetch("/api/testimonials?featured=1");
      if (!res.ok) throw new Error("Failed to load testimonials");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const items = (data && data.length > 0 ? data : TESTIMONIALS_FALLBACK).slice(0, 6);

  return (
    <section id="testimonials" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">
            {t("testimonials.title")}
          </h2>
          <p className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("testimonials.subtitle")}
          </p>
        </div>

        {isLoading && !data ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-muted/20 rounded-2xl p-6 sm:p-8 animate-pulse h-56" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {items.map((item) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
